// Domain rules shared by the server and the web app. Source: PRD v1.0 in docs/.

export type StorageType = '1-1' | '1-2' | '2-1' | '2-2' | '2-3';
export type AreaType = StorageType | '3';
export type Pos = 'L' | 'M' | 'R';
export type Role = 'owner' | 'user';
export type ItemStatus = 'in' | 'out' | 'gone';
export type QcResult = 'Pass' | 'Fail';

/** Storage types decide the home area (PRD §5). */
export const TYPES: Record<StorageType, { name: string; home: string }> = {
  '1-1': { name: 'Workshop tool', home: 'W1' },
  '1-2': { name: 'Installation tool', home: 'S3' },
  '2-1': { name: 'Product', home: 'L1' },
  '2-2': { name: 'Material in pieces', home: 'S1' },
  '2-3': { name: 'Material by amount', home: 'S2' },
};
export const STORAGE_TYPES = Object.keys(TYPES) as StorageType[];
export const AREA_TYPES: AreaType[] = [...STORAGE_TYPES, '3'];
export const CONSUMABLE_HOME = 'S2';
export const REPAIR_AREA_TYPE = '3';
export const LOCKED_AREA_TYPE = '2-1';

/** Number categories, the CC part of the item number (PRD §6). */
export const NUMCATS: Record<string, { name: string; long: boolean }> = {
  '02': { name: 'R&D', long: true },
  '03': { name: 'Project', long: true },
  '06': { name: 'Consumables', long: false },
  '07': { name: 'Tools', long: false },
};
export const FREE_STOCK = '0301';
export const REPAIR = ['Waiting for repair', 'In repair', 'Out for repair', 'Unused'] as const;
export const GONE = ['Lost', 'Thrown away'] as const;
export const POS: Record<Pos, string> = { L: 'Left', M: 'Middle', R: 'Right' };
export const OUTWHY = [
  'Installation on site',
  'Project or customer',
  'Using in the workshop',
  'Testing in the lab',
  'Repair outside',
  'Other',
] as const;
export const ROLE_LABEL: Record<Role, string> = { owner: 'Warehouse owner', user: 'User' };
export const MAX_USERS = 8;
export const QC_INTERVALS = [3, 6, 12, 18, 24, 36];
export const MIN_YEAR = 26;

export const ACTIONS = [
  'Register',
  'Take out',
  'Move',
  'QC check',
  'Suitcase check',
  'Daily report',
  'Approval request',
  'Approval',
  'Status',
  'Note',
  'Replaced',
  'Article',
  'Setup',
] as const;
export type Action = (typeof ACTIONS)[number];

// ---------------- item numbers ----------------

const NUMRE = /^PH-(\d{2})-(\d{2})-(?:(\d{4})-)?(\d{3})$/;

export type ParsedNo =
  | { ok: true; yy: string; cc: string; proj: string; n: number }
  | { ok: false; err: string };

export function normNo(no: unknown): string {
  return String(no ?? '').trim().toUpperCase();
}

export function checkNo(no: unknown): ParsedNo {
  const m = NUMRE.exec(normNo(no));
  if (!m) return { ok: false, err: 'Format must be PH-YY-CC-NNN or PH-YY-CC-PPPP-NNN' };
  const [, yy, cc, proj, n] = m as unknown as [string, string, string, string | undefined, string];
  const c = NUMCATS[cc];
  if (!c) return { ok: false, err: `Category ${cc} is not used in this version (only 02, 03, 06, 07)` };
  if (c.long && !proj) return { ok: false, err: `Category ${cc} needs the long format with a 4-digit project code` };
  if (!c.long && proj) return { ok: false, err: `Category ${cc} uses the short format, without a project code` };
  if (proj && proj.slice(0, 2) !== cc) return { ok: false, err: `Project code ${proj} does not belong to category ${cc}` };
  if (+yy < MIN_YEAR) return { ok: false, err: `Year must be ${MIN_YEAR} or later` };
  if (n === '000') return { ok: false, err: 'Item number starts at 001' };
  return { ok: true, yy, cc, proj: proj ?? '', n: +n };
}

/** One counter per category per year (short) or per project per year (long). */
export function groupKey(yy: string, cc: string, proj: string): string {
  return cc + (proj ? '-' + proj : '') + '/' + yy;
}

export function formatNo(yy: string, cc: string, proj: string, n: number): string {
  return `PH-${yy}-${cc}-${proj ? proj + '-' : ''}${String(n).padStart(3, '0')}`;
}

/** Which number categories fit an article (PRD §6 "How the category is chosen"). */
export function allowedCC(a: { type: StorageType; consumable: boolean }): string[] {
  if (a.type === '1-1' || a.type === '1-2') return ['07'];
  if (a.consumable) return ['06'];
  return ['02', '03'];
}

export function defaultCC(a: { type: StorageType; consumable: boolean }): string {
  const cc = allowedCC(a);
  return cc.includes('03') ? '03' : cc[0]!;
}

export const isProjectCode = (c: string) => /^0[23]\d{2}$/.test(c);

// ---------------- places ----------------

export interface Loc {
  shelf: string;
  level: number;
  pos: Pos;
}

export const addr = (l: Loc | null | undefined) => (l ? `${l.shelf}-${l.level}-${l.pos}` : '');

const PLACERE = /^([A-Z]\d{1,2})-(\d)-([LMR])$/;
export function parsePlace(s: string): Loc | null {
  const m = PLACERE.exec(String(s ?? '').trim().toUpperCase());
  return m ? { shelf: m[1]!, level: +m[2]!, pos: m[3] as Pos } : null;
}

export function homeOf(a: { type: StorageType; consumable: boolean }): string {
  return a.consumable ? CONSUMABLE_HOME : TYPES[a.type].home;
}

export function typeLabel(a: { type: StorageType; productPart?: boolean; consumable?: boolean }): string {
  return `${a.type} ${TYPES[a.type]?.name ?? '?'}${a.productPart ? ', product part' : ''}${a.consumable ? ', consumable' : ''}`;
}

/** Products always need QC; parts of our product need QC (PRD §10). */
export function defaultQcNeeded(type: StorageType, productPart: boolean): boolean {
  return type === '2-1' || (type === '2-2' && productPart);
}

/** An item is reserved when its number carries a project code, except free stock 0301 (PRD §8). */
export const isReserved = (proj: string | null | undefined) => !!proj && proj !== FREE_STOCK;

// ---------------- dates (YYYY-MM-DD strings) ----------------

export function ymdInTz(d: Date, tz: string): string {
  // en-CA formats as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

export function addDays(ymd: string, n: number): string {
  const x = new Date(ymd + 'T12:00:00Z');
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
}

export function addMonths(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + n, 1, 12));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0, 12)).getUTCDate();
  target.setUTCDate(Math.min(d, last));
  return target.toISOString().slice(0, 10);
}

export function mondayOf(ymd: string): string {
  const x = new Date(ymd + 'T12:00:00Z');
  return addDays(ymd, -((x.getUTCDay() + 6) % 7));
}

// ---------------- QC ----------------

export type QcKind = 'na' | 'none' | 'fail' | 'over' | 'soon' | 'ok';
export interface QcState {
  k: QcKind;
  cls: 'soft' | 'bad' | 'warn' | 'ok';
  t: string;
  ok: boolean;
  due?: string;
}

export function qcState(
  needed: boolean,
  qc: { last: string | null; result: QcResult | null },
  everyMonths: number,
  today: string,
): QcState {
  if (!needed) return { k: 'na', cls: 'soft', t: 'No QC needed', ok: true };
  if (!qc.last) return { k: 'none', cls: 'bad', t: 'QC missing', ok: false };
  if (qc.result === 'Fail') return { k: 'fail', cls: 'bad', t: 'QC failed', ok: false };
  const due = addMonths(qc.last, everyMonths || 12);
  if (due < today) return { k: 'over', cls: 'bad', t: `QC due again (since ${due})`, due, ok: false };
  if (due <= addDays(today, 30)) return { k: 'soon', cls: 'warn', t: `QC due on ${due}`, due, ok: true };
  return { k: 'ok', cls: 'ok', t: `QC OK until ${due}`, due, ok: true };
}

export const round2 = (n: number) => Math.round(n * 100) / 100;
